import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bot, Building2, Loader2, Plus } from 'lucide-react';
import { useAiInterview } from '../hooks/useAiInterview';
import { applicationsService } from '../services/api';
import { ApplicationDetail, AiInterviewPayload } from '../types';
import { INTERVIEW_DIFFICULTY_META, INTERVIEW_TYPE_META } from '../components/interview/interviewConstants';
import InterviewSetupForm from '../components/interview/InterviewSetupForm';
import InterviewReadyCard from '../components/interview/InterviewReadyCard';
import ConversationLog from '../components/interview/ConversationLog';
import InterviewControls from '../components/interview/InterviewControls';
import InterviewResultCard from '../components/interview/InterviewResultCard';
import InterviewErrorState from '../components/interview/InterviewErrorState';
import InterviewSkeleton from '../components/interview/InterviewSkeleton';

const DEFAULT_PAYLOAD: AiInterviewPayload = {
  interviewType: 'TECHNICAL',
  difficulty: 'MID_LEVEL',
  maxQuestions: 8,
  maxFollowups: 2,
  maxDurationSeconds: 30 * 60,
};

const AiInterview: React.FC = () => {
  const { applicationId: rawId } = useParams<{ applicationId: string }>();
  const applicationId = rawId ? Number(rawId) : NaN;
  const navigate = useNavigate();

  const {
    loading,
    loadError,
    interviews,
    selected,
    completed,
    session,
    stage,
    driverState,
    aiSpeaking,
    micOn,
    micError,
    transcript,
    answeredCount,
    maxQuestions,
    isMock,
    createInterview,
    begin,
    submitAnswer,
    finishInterview,
    deleteInterview,
    reload,
  } = useAiInterview(Number.isFinite(applicationId) ? applicationId : -1);

  const [application, setApplication] = useState<ApplicationDetail | null>(null);
  const [starting, setStarting] = useState(false);
  // When true, the setup form is shown even though a completed interview exists
  // (the "Start another interview" flow), prefilled with the last interview's
  // selections so the user can review and change them before the next attempt.
  const [showingNewSetup, setShowingNewSetup] = useState(false);

  useEffect(() => {
    if (!Number.isFinite(applicationId)) return;
    let cancelled = false;
    applicationsService
      .get(applicationId)
      .then((res) => {
        if (!cancelled) setApplication(res.data);
      })
      .catch(() => {
        if (!cancelled) setApplication(null);
      });
    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  const goBack = useCallback(() => navigate('/applications'), [navigate]);

  const handleBegin = useCallback(async () => {
    setStarting(true);
    try {
      await begin();
    } catch {
      // Error is surfaced through loadError.
    } finally {
      setStarting(false);
    }
  }, [begin]);

  const handleStartAnother = useCallback(() => {
    setShowingNewSetup(true);
  }, []);

  const handleSetupSubmit = useCallback(
    async (payload: AiInterviewPayload) => {
      try {
        await createInterview(payload);
        setShowingNewSetup(false);
      } catch {
        // Error is surfaced through the form.
      }
    },
    [createInterview]
  );

  const handleSetupCancel = useCallback(() => setShowingNewSetup(false), []);

  // Prefill defaults from the interview that was just finished so "Start another
  // interview" lets the candidate tweak their previous choices, not start blind.
  const setupDefaults = useMemo<AiInterviewPayload>(() => {
    const source = completed ?? selected;
    if (!source) return DEFAULT_PAYLOAD;
    return {
      interviewType: source.interviewType,
      difficulty: source.difficulty,
      maxQuestions: source.maxQuestions,
      maxFollowups: source.maxFollowups,
      maxDurationSeconds: source.maxDurationSeconds,
      jobDescription: source.jobDescription ?? undefined,
      customQuestions: source.config?.customQuestions ?? [],
    };
  }, [completed, selected]);

  const handleDiscard = useCallback(async () => {
    if (!selected) return;
    try {
      await deleteInterview(selected.id);
    } catch (err) {
      console.error(err);
    }
  }, [selected, deleteInterview]);

  if (!Number.isFinite(applicationId)) {
    return <InterviewErrorState message="This interview link is not valid." onRetry={goBack} onBack={goBack} />;
  }

  const header = (
    <div className="mb-6">
      <button onClick={goBack} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-foreground transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Applications
      </button>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-btn bg-brand-50 flex items-center justify-center shrink-0">
            <Bot className="w-6 h-6 text-brand-500" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-foreground leading-tight">AI Voice Interview</h1>
            <p className="text-sm text-zinc-500 flex items-center gap-1.5">
              {application ? (
                <>
                  <Building2 className="w-3.5 h-3.5" />
                  {application.jobTitle}
                  {application.organizationName ? ` · ${application.organizationName}` : ''}
                </>
              ) : (
                'Practice your responses with a live interviewer.'
              )}
            </p>
          </div>
        </div>
        {isMock && (
          <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
            Mock mode — simulated answers
          </span>
        )}
      </div>
    </div>
  );

  let content: React.ReactNode;

  if (loading) {
    content = <InterviewSkeleton />;
  } else if (loadError && interviews.length === 0) {
    content = <InterviewErrorState message={loadError} onRetry={reload} onBack={goBack} />;
  } else if (showingNewSetup) {
    content = (
      <InterviewSetupForm
        applicationTitle={application ? `${application.jobTitle}${application.organizationName ? ` · ${application.organizationName}` : ''}` : undefined}
        initialValues={setupDefaults}
        onCancel={handleSetupCancel}
        onSubmit={handleSetupSubmit}
      />
    );
  } else if (session) {
    const finishing = stage === 'COMPLETING';
    const current = selected;
    content = (
      <div className="flex flex-col h-[72vh] gap-4">
        {loadError && (
          <p className="text-sm text-red-600 bg-red-50 rounded-btn px-3 py-2">{loadError}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {current && (
            <>
              <span className={`text-[11px] font-semibold px-2 py-1 rounded ${INTERVIEW_TYPE_META[current.interviewType].badge}`}>
                {INTERVIEW_TYPE_META[current.interviewType].label}
              </span>
              <span className={`text-[11px] font-semibold px-2 py-1 rounded ${INTERVIEW_DIFFICULTY_META[current.difficulty ?? 'MID_LEVEL'].badge}`}>
                {INTERVIEW_DIFFICULTY_META[current.difficulty ?? 'MID_LEVEL'].label}
              </span>
            </>
          )}
        </div>
        {stage === 'ERROR' ? (
          <div className="flex items-center justify-center flex-1">
            <div className="flex flex-col items-center gap-4">
              <p className="text-sm text-zinc-600 text-center">
                The connection to the interviewer was lost. You can reconnect or end the interview.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={handleBegin}
                  className="h-10 px-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-btn transition-colors"
                >
                  <Loader2 className="w-4 h-4" /> Reconnect
                </button>
                <button
                  onClick={() => void finishInterview()}
                  className="h-10 px-4 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-btn transition-colors"
                >
                  End and evaluate
                </button>
              </div>
            </div>
          </div>
        ) : (
          <ConversationLog lines={transcript} live aiSpeaking={aiSpeaking} micOn={micOn} stage={stage} />
        )}
        <InterviewControls
          stage={stage}
          micOn={micOn}
          micError={micError}
          answeredCount={answeredCount}
          maxQuestions={maxQuestions || 10}
          isMock={isMock}
          finishing={finishing}
          onFinish={() => void finishInterview()}
          onSubmitMockAnswer={submitAnswer}
        />
      </div>
    );
  } else if (selected) {
    content = (
      <InterviewReadyCard
        interview={selected}
        busy={starting || driverState === 'connecting'}
        onStart={handleBegin}
        onDelete={() => void handleDiscard()}
      />
    );
  } else if (completed) {
    if (completed.result) {
      content = (
        <div className="space-y-4">
          <InterviewResultCard result={completed.result} />
          {completed.transcript.length > 0 && (
            <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-6">
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wide mb-3">Full transcript</h4>
              <div className="h-80 flex flex-col min-h-0">
                <ConversationLog lines={completed.transcript} />
              </div>
            </div>
          )}
          <div className="flex justify-center gap-3 pt-2">
            <button
              onClick={handleStartAnother}
              className="h-10 px-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-btn transition-colors"
            >
              <Plus className="w-4 h-4" />
              Start another interview
            </button>
            <button
              onClick={goBack}
              className="h-10 px-4 text-sm font-medium text-zinc-600 hover:bg-zinc-50 rounded-btn transition-colors"
            >
              Back to applications
            </button>
          </div>
        </div>
      );
    } else {
      // COMPLETED but no result yet (evaluation still processing).
      content = (
        <div className="max-w-md mx-auto">
          <div className="border border-zinc-200 rounded-card shadow-soft bg-white p-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-brand-500 mx-auto mb-3" />
            <h3 className="font-display font-semibold text-foreground mb-1">Evaluating your answers…</h3>
            <p className="text-sm text-zinc-500 mb-5">
              The interview is complete. The evaluation takes a few seconds.
            </p>
            <button
              onClick={() => void reload()}
              className="h-10 px-4 text-sm font-medium text-brand-600 bg-brand-50 hover:bg-brand-100 rounded-btn transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
  } else {
    content = (
      <InterviewSetupForm
        applicationTitle={application ? `${application.jobTitle}${application.organizationName ? ` · ${application.organizationName}` : ''}` : undefined}
        onCancel={goBack}
        onSubmit={handleSetupSubmit}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {header}
      {content}
    </div>
  );
};

export default AiInterview;